import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Brain, FileDown, Lock } from "lucide-react";
import { UpgradeNudge } from "@/components/entitlements/LockedPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ActionBar = ({ legislator }) => (
  <div className="border-b border-white/10" data-testid="profile-action-bar" style={{ background: "var(--whip-surface)" }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
      <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/45 truncate">
        Profile · {legislator?.name}
      </p>
      <button
        type="button"
        disabled
        title="One-page PDF brief, arriving in the next profile update"
        className="inline-flex items-center gap-1.5 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs font-semibold text-orange-200 opacity-70 cursor-not-allowed"
        data-testid="generate-brief-btn"
      >
        <FileDown className="w-3.5 h-3.5" /> Generate Brief
      </button>
    </div>
  </div>
);

export const StrategicRead = ({ legislatorId }) => {
  const [read, setRead] = useState(null);
  const [building, setBuilding] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setRead(null);
    setBuilding(true);
    // First view in this browser session asks the backend to refresh a read older than an hour.
    const sessionKey = `whip:read-session:${legislatorId}`;
    const firstThisSession = !sessionStorage.getItem(sessionKey);
    const poll = async (attempt, session, prevGeneratedAt) => {
      try {
        const r = await axios.get(`${API}/legislators/${legislatorId}/strategic-read`, { params: session ? { session: true } : {}, validateStatus: (s) => s === 200 || s === 202 });
        if (cancelled) return;
        if (session) sessionStorage.setItem(sessionKey, "1");
        if (r.status === 202) {
          if (attempt < 14) timer.current = setTimeout(() => poll(attempt + 1, false), 5000);
          else setBuilding(false);
          return;
        }
        setRead(r.data);
        setBuilding(false);
        // A refresh is running in the background: pick up the new text when it lands.
        if (r.data.refreshing && attempt < 10 && r.data.generatedAt === (prevGeneratedAt ?? r.data.generatedAt)) {
          timer.current = setTimeout(() => poll(attempt + 1, false, r.data.generatedAt), 8000);
        }
      } catch {
        if (!cancelled) setBuilding(false);
      }
    };
    poll(0, firstThisSession);
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [legislatorId]);

  if (!read && !building) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="rounded-xl border border-orange-500/25 p-5" style={{ background: "linear-gradient(135deg, rgba(255,106,0,0.08), rgba(11,11,15,0.6))" }} data-testid="strategic-read">
        <div className="flex items-center gap-2 mb-2">
          <Brain className="w-4 h-4 text-orange-400" />
          <p className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-orange-300/90">Strategic Read</p>
          {read?.generatedAt && (
            <span className="ml-auto text-[10px] font-mono text-white/35" data-testid="strategic-read-updated">
              {read.refreshing ? "Refreshing with latest data…" : `Refreshed ${new Date(read.generatedAt).toLocaleDateString()}`}
            </span>
          )}
        </div>
        {read ? (
          <>
            <p className="text-sm md:text-base leading-relaxed text-white/90" data-testid="strategic-read-text">{read.summary}</p>
            {read.bullets?.length > 0 && (
              <ul className="mt-4 grid gap-1.5 sm:grid-cols-2" data-testid="strategic-read-bullets">
                {read.bullets.map((b, i) => read.preview ? (
            <li key={i} className="flex items-center gap-2 text-sm text-white/50" data-testid={`strategic-read-bullet-${i}`}>
              <Lock className="w-3 h-3 text-orange-400 shrink-0" /><span className="font-semibold text-white/70">{b.title}</span>
              <span className="text-white/30 tracking-widest select-none">••••••••••••••••••</span>
            </li>
          ) : (
                  <li key={i} className="flex gap-2 text-sm text-white/85" data-testid={`strategic-read-bullet-${i}`}>
                    <span className="text-orange-400 mt-[2px]">▸</span>
                    <span><span className="font-semibold text-white">{b.title}:</span> {b.value}</span>
                  </li>
                ))}
              </ul>
            )}
            {read.preview && (
              <UpgradeNudge feature="strategic_read" tier="pro" className="mt-4"
                text="Preview: Pro members see the full read, including where this legislator is persuadable and who to go through." />
            )}
          </>
        ) : (
          <div className="flex items-center gap-3 text-sm text-white/55" data-testid="strategic-read-building">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-400" />
            Analyst read in progress, usually under 30 seconds.
          </div>
        )}
      </div>
    </div>
  );
};

export const NewsTab = ({ legislatorId }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    axios.get(`${API}/legislators/${legislatorId}/news`, { params: { limit: 24 } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [legislatorId]);

  const Section = ({ title, subtitle, list, testId, empty }) => (
    <div className="whip-surface rounded-xl border border-white/10 p-5" data-testid={testId}>
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <p className="text-xs text-white/45 mb-4">{subtitle}</p>
      {items === null ? (
        <p className="text-sm text-white/50">Loading coverage…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-white/50">{empty}</p>
      ) : (
        <ul className="space-y-3">
          {list.map((n, i) => (
            <li key={i} className="border border-white/10 rounded-lg p-3 hover:border-orange-400/50 transition-colors">
              <a href={n.link} target="_blank" rel="noopener noreferrer" className="block">
                <p className="text-sm font-medium text-white/90 line-clamp-2">{n.title}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-white/45 flex-wrap">
                  {n.legislativeTag && (
                    <span className="px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/30 text-[10px] uppercase tracking-wider">{n.legislativeTag}</span>
                  )}
                  <span>{n.source}</span>
                  {n.publishedAt && <span>· {new Date(n.publishedAt).toLocaleDateString()}</span>}
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const legislative = (items || []).filter((n) => n.legislative);
  const general = (items || []).filter((n) => !n.legislativeSource);
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="profile-tab-panel-news">
      <Section title="Legislative Coverage" subtitle="Bill, vote, committee and floor coverage from The Hill, Roll Call, Politico, Reuters, AP and GovTrack, tagged with the bill number where detected." list={legislative} testId="news-legislative" empty="No recent legislative coverage found." />
      <Section title="General Coverage via Google News" subtitle={<>Unfiltered feed, newest first. Source: <a href="https://news.google.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-300">Google News</a>.</>} list={general} testId="news-general" empty="No recent news found." />
    </div>
  );
};
